/**
 * Country configuration registry.
 * Source of truth for all country-specific behavior in ShopLedger.
 * To add a new country, add a new entry to COUNTRIES and its regions.
 */

export interface Region {
  code: string;
  name: string;
}

export interface AddressFormat {
  /** Ordered list of fields to show in address forms */
  fields: ("line1" | "line2" | "city" | "state" | "zip" | "province" | "postal_code" | "country")[];
  /** Label overrides for address fields */
  labels: Record<string, string>;
  /** Placeholder overrides */
  placeholders?: Record<string, string>;
}

export interface CountryConfig {
  code: string;
  name: string;
  flag: string;
  /** ISO 4217 currency code */
  currency: string;
  /** Default BCP-47 locale */
  defaultLocale: string;
  /** Supported locales */
  locales: string[];
  /** IANA timezone list for the timezone picker */
  timezones: { value: string; label: string }[];
  /** Default IANA timezone for new shops */
  defaultTimezone: string;
  /** Sub-jurisdictions (US states, Spain comunidades autónomas, etc.) */
  regions: Region[];
  /** Label for the region selector (e.g. "State", "Community") */
  regionLabel: string;
  /** Whether a region is required during shop creation */
  regionRequired: boolean;
  /** Phone number placeholder */
  phonePlaceholder: string;
  /** Address format configuration */
  addressFormat: AddressFormat;
  /** Country-specific product features (US banking/tax rails are not available elsewhere) */
  features: CountryFeatures;
}

export interface CountryFeatures {
  /** Plaid bank links + ACH direct-deposit payouts (Increase) */
  usBankPayouts: boolean;
  /** IRS W-9 / 1099 tax info collection */
  usTaxForms: boolean;
}

// ─── United States ───────────────────────────────────────────────────────────

const US_REGIONS: Region[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "VI", name: "Virgin Islands" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HST)" },
  { value: "America/Indiana/Indianapolis", label: "Indiana (ET)" },
  { value: "America/Detroit", label: "Michigan (ET)" },
  { value: "America/Boise", label: "Boise (MT)" },
  { value: "America/Puerto_Rico", label: "Puerto Rico (AST)" },
  { value: "America/Virgin", label: "Virgin Islands (AST)" },
];

// ─── Spain ───────────────────────────────────────────────────────────────────

const ES_REGIONS: Region[] = [
  { code: "AN", name: "Andalucía" },
  { code: "AR", name: "Aragón" },
  { code: "AS", name: "Asturias" },
  { code: "IB", name: "Illes Balears" },
  { code: "CN", name: "Canarias" },
  { code: "CB", name: "Cantabria" },
  { code: "CL", name: "Castilla y León" },
  { code: "CM", name: "Castilla-La Mancha" },
  { code: "CT", name: "Catalunya" },
  { code: "CE", name: "Ceuta" },
  { code: "VC", name: "Comunitat Valenciana" },
  { code: "EX", name: "Extremadura" },
  { code: "GA", name: "Galicia" },
  { code: "MD", name: "Madrid" },
  { code: "ML", name: "Melilla" },
  { code: "MC", name: "Murcia" },
  { code: "NC", name: "Navarra" },
  { code: "PV", name: "País Vasco" },
  { code: "RI", name: "La Rioja" },
];

const ES_TIMEZONES = [
  { value: "Europe/Madrid", label: "Península (CET)" },
  { value: "Atlantic/Canary", label: "Canarias (WET)" },
];

// ─── Country Registry ────────────────────────────────────────────────────────

export const COUNTRIES: Record<string, CountryConfig> = {
  US: {
    code: "US",
    name: "United States",
    flag: "🇺🇸",
    currency: "USD",
    defaultLocale: "en-US",
    locales: ["en-US"],
    timezones: US_TIMEZONES,
    defaultTimezone: "America/Chicago",
    regions: US_REGIONS,
    regionLabel: "State",
    regionRequired: true,
    phonePlaceholder: "(555) 123-4567",
    features: { usBankPayouts: true, usTaxForms: true },
    addressFormat: {
      fields: ["line1", "line2", "city", "state", "zip"],
      labels: { line1: "Street Address", line2: "Apt / Suite", city: "City", state: "State", zip: "ZIP Code" },
      placeholders: { zip: "12345" },
    },
  },
  ES: {
    code: "ES",
    name: "España",
    flag: "🇪🇸",
    currency: "EUR",
    defaultLocale: "es-ES",
    locales: ["es-ES", "en-US"],
    timezones: ES_TIMEZONES,
    defaultTimezone: "Europe/Madrid",
    regions: ES_REGIONS,
    regionLabel: "Comunidad Autónoma",
    regionRequired: false,
    phonePlaceholder: "+34 612 345 678",
    features: { usBankPayouts: false, usTaxForms: false },
    addressFormat: {
      fields: ["line1", "line2", "postal_code", "city", "province"],
      labels: { line1: "Dirección", line2: "Piso / Puerta", city: "Ciudad", province: "Provincia", postal_code: "Código Postal" },
      placeholders: { postal_code: "28001" },
    },
  },
};

/** Ordered list for UI dropdowns */
export const COUNTRY_LIST = Object.values(COUNTRIES).sort((a, b) => a.name.localeCompare(b.name));

/** Get country config, defaulting to US */
export function getCountryConfig(countryCode: string | null | undefined): CountryConfig {
  return COUNTRIES[countryCode || "US"] || COUNTRIES.US;
}

/** Get the currency for a country code */
export function getCurrency(countryCode: string | null | undefined): string {
  return getCountryConfig(countryCode).currency;
}

/** Get the default locale for a country code */
export function getDefaultLocale(countryCode: string | null | undefined): string {
  return getCountryConfig(countryCode).defaultLocale;
}
